import { ApplicationInsights, type ITelemetryItem } from '@microsoft/applicationinsights-web';

import { APP_NAME } from '~config';

type TelemetryProperties = Record<string, string | undefined>;

type PageViewTelemetry = {
  readonly pathname: string;
  readonly routeId?: string;
  readonly routeTemplate?: string;
};

type MetricTelemetry = {
  readonly name: string;
  readonly value: number;
  readonly properties?: TelemetryProperties;
};

const APP_INSIGHTS_CONNECTION_STRING = import.meta.env.VITE_APP_APPINSIGHTS_CONNECTION_STRING?.trim();
const APP_SLUG = APP_NAME.toLowerCase().replace(/\s+/g, '-');
const SESSION_STORAGE_KEY = `${APP_SLUG}:telemetry-session-id`;

let initialized = false;
let sessionId = '';
let currentAuthenticatedUserId: string | null = null;
let currentUserAccountId: string | null = null;
let lastTrackedPathname: string | null = null;

const createId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

const getSessionId = (): string => {
  if (sessionId) return sessionId;

  if (typeof window === 'undefined') {
    sessionId = createId();
    return sessionId;
  }

  const existingSessionId = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (existingSessionId) {
    sessionId = existingSessionId;
    return sessionId;
  }

  sessionId = createId();
  window.sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  return sessionId;
};

const sanitizeProperties = (properties?: TelemetryProperties): Record<string, string> => {
  if (!properties) return {};

  return Object.fromEntries(
    Object.entries(properties).flatMap(([key, value]) => {
      if (value == null || value === '') return [];
      return [[key, String(value)]];
    }),
  );
};

const normalizeError = (error: unknown): Error => {
  if (error instanceof Error) return error;
  if (typeof error === 'string') return new Error(error);

  return new Error('Unknown error');
};

const telemetryInitializer = (item: ITelemetryItem): void => {
  item.tags = item.tags ?? {};
  item.tags['ai.application.ver'] = import.meta.env.MODE;
  item.tags['ai.cloud.role'] = APP_NAME;
  item.tags['ai.device.id'] = 'browser';
  item.tags['ai.device.type'] = 'Browser';

  const sid = getSessionId();
  item.tags['ai.operation.id'] = sid;
  item.tags['ai.session.id'] = sid;

  if (currentAuthenticatedUserId) {
    item.tags['ai.user.authUserId'] = currentAuthenticatedUserId;
  }

  if (currentUserAccountId) {
    item.tags['ai.user.id'] = currentUserAccountId;
  }
};

const appInsights = APP_INSIGHTS_CONNECTION_STRING
  ? new ApplicationInsights({
      config: {
        connectionString: APP_INSIGHTS_CONNECTION_STRING,
        disableAjaxTracking: true,
        disableFetchTracking: true,
        enableAutoRouteTracking: false,
        namePrefix: `${APP_SLUG}-`,
      },
    })
  : null;

const initTelemetry = (): void => {
  if (initialized) return;

  initialized = true;

  if (!appInsights) return;

  appInsights.loadAppInsights();
  appInsights.addTelemetryInitializer((item) => {
    telemetryInitializer(item);
  });
};

const isTelemetryEnabled = (): boolean => appInsights !== null;

const trackPageView = ({ pathname, routeId, routeTemplate }: PageViewTelemetry): void => {
  if (!appInsights || lastTrackedPathname === pathname) return;

  lastTrackedPathname = pathname;

  appInsights.trackPageView({
    name: routeTemplate ?? pathname,
    uri: typeof window === 'undefined' ? pathname : `${window.location.origin}${pathname}`,
    properties: sanitizeProperties({
      pathname,
      routeId,
      routeTemplate,
      mode: import.meta.env.MODE,
    }),
  });
};

const trackEvent = (name: string, properties?: TelemetryProperties): void => {
  if (!appInsights) return;

  appInsights.trackEvent(
    { name },
    sanitizeProperties({
      ...properties,
      mode: import.meta.env.MODE,
    }),
  );
};

const trackMetric = ({ name, value, properties }: MetricTelemetry): void => {
  if (!appInsights) return;

  appInsights.trackMetric(
    { name, average: value },
    sanitizeProperties({
      ...properties,
      mode: import.meta.env.MODE,
    }),
  );
};

const trackException = (error: unknown, properties?: TelemetryProperties): void => {
  if (!appInsights) return;

  appInsights.trackException(
    { exception: normalizeError(error) },
    sanitizeProperties({
      ...properties,
      mode: import.meta.env.MODE,
    }),
  );
};

const setAuthenticatedUserContext = (homeAccountId: string): void => {
  currentAuthenticatedUserId = homeAccountId;
  currentUserAccountId = homeAccountId;
  appInsights?.setAuthenticatedUserContext(homeAccountId, homeAccountId, false);
};

const clearAuthenticatedUserContext = (): void => {
  currentAuthenticatedUserId = null;
  currentUserAccountId = null;
  lastTrackedPathname = null;
  appInsights?.clearAuthenticatedUserContext();
};

export {
  clearAuthenticatedUserContext,
  initTelemetry,
  isTelemetryEnabled,
  setAuthenticatedUserContext,
  trackEvent,
  trackException,
  trackMetric,
  trackPageView,
};
